import React, { useEffect, useState, useRef, useContext, useMemo } from "react";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";
import { AuthContext } from "../../context/AuthContext";
import {
  ProfileContainer,
  ProfileHeader,
  ProfileDetails,
  ProfileDetailItem,
  ProfilePictureWrap,
  ProfileAvatar,
  UploadHint,
  HiddenFileInput,
  StatusMessage,
  ReferralCard,
  ReferralLabel,
  ReferralCode,
  ReferralSub,
  CopyBtn,
  StatsGrid,
  StatCard,
  StatLabel,
  StatValue,
  StatSub,
} from "./ChauffeurProfileElements";

const normalizeRole = (role) => {
  if (!role) return "client";

  const r = String(role).toLowerCase().trim();

  if (
    r === "driver" ||
    r === "chauffeur" ||
    r === "chauffeur_app" ||
    r === "driver_app"
  ) {
    return "chauffeur";
  }

  return r;
};

const Profile = () => {
  const API = process.env.REACT_APP_API_URL || "http://localhost:5002";

  const auth = useContext(AuthContext) || {};

  const contextRole = auth.role || null;
  const contextToken = auth.token || null;
  const contextIsAuthenticated = auth.isAuthenticated || false;

  const fallbackToken =
    localStorage.getItem("token") ||
    localStorage.getItem("clientToken") ||
    localStorage.getItem("chauffeurToken");

  const fallbackRole =
    localStorage.getItem("userType") ||
    localStorage.getItem("role") ||
    localStorage.getItem("accountType");

  const token = contextToken || fallbackToken;

  const normalizedRole = useMemo(
    () => normalizeRole(contextRole || fallbackRole),
    [contextRole, fallbackRole]
  );

  const isAuthenticated = contextIsAuthenticated || !!token;
  const isDriver = normalizedRole === "chauffeur";

  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [successMessage, setSuccessMessage] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const fileRef = useRef(null);

  const profileEndpoint = isDriver
    ? `${API}/api/chauffeurs/profile-chauffeur`
    : `${API}/api/clients/profile-client`;

  const uploadPhotoEndpoint = `${API}/api/clients/upload-photo`;

  useEffect(() => {
    AOS.init({
      duration: 1200,
      once: false,
      mirror: true,
    });
  }, []);

  const getPhotoUrl = (photoValue) => {
    if (!photoValue) return null;

    const value = String(photoValue);

    if (value.startsWith("http")) return value;
    if (value.startsWith("/uploads")) return `${API}${value}`;

    return `${API}/uploads/profile-photos/${value}`;
  };

  const fetchProfile = async () => {
    try {
      setError(null);

      if (!token || !isAuthenticated) {
        setUserInfo(null);
        setLoading(false);
        setError("Vous n’êtes pas connecté.");
        return;
      }

      const res = await axios.get(profileEndpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const profileData = res.data || null;

      setUserInfo(profileData);
      setPhoto(profileData?.photo || null);
      setLoading(false);

      setTimeout(() => {
        AOS.refresh();
      }, 100);
    } catch (err) {
      console.error("Erreur profil:", err);
      setError("Impossible de charger le profil. Veuillez réessayer.");
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileEndpoint, token, isAuthenticated]);

  const openFilePicker = () => {
    if (isDriver || isUploading) return;
    fileRef.current?.click();
  };

  const handlePhotoUpload = async (e) => {
    if (isDriver) return;

    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("photo", file);

    try {
      setError(null);
      setSuccessMessage(null);
      setIsUploading(true);

      if (!token) {
        setError("Vous devez être connecté pour modifier la photo.");
        return;
      }

      const res = await axios.post(uploadPhotoEndpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const uploadedPhoto = res.data?.photo || null;

      if (uploadedPhoto) {
        setPhoto(uploadedPhoto);
      }

      setSuccessMessage("Photo de profil mise à jour ✅");
      await fetchProfile();
    } catch (err) {
      console.error("Erreur upload photo:", err);
      console.log("Upload response data:", err?.response?.data);

      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Erreur lors de l’upload de la photo.";

      setError(backendMessage);
    } finally {
      setIsUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const referralCode = userInfo?.referralCode || null;

  const driverRating =
    userInfo?.driverRating ??
    userInfo?.rating ??
    userInfo?.averageRating ??
    userInfo?.note ??
    null;

  const totalRides =
    userInfo?.totalRides ??
    userInfo?.ridesCount ??
    userInfo?.numberOfRides ??
    userInfo?.tripCount ??
    0;

  const handleCopyReferral = async () => {
    if (!referralCode) return;

    try {
      await navigator.clipboard.writeText(referralCode);
      setError(null);
      setSuccessMessage("Code copié ✅");
      setTimeout(() => setSuccessMessage(null), 1200);
    } catch (err) {
      setError("Impossible de copier. Copie-le manuellement.");
    }
  };

  if (loading) {
    return (
      <ProfileContainer>
        <ProfileHeader data-aos="fade-up">
          <h1>{isDriver ? "Profil Chauffeur" : "Profil Client"}</h1>
        </ProfileHeader>
        <StatusMessage data-aos="fade-up">
          Chargement des informations...
        </StatusMessage>
      </ProfileContainer>
    );
  }

  if (error && !userInfo) {
    return (
      <ProfileContainer>
        <ProfileHeader data-aos="fade-up">
          <h1>{isDriver ? "Profil Chauffeur" : "Profil Client"}</h1>
        </ProfileHeader>
        <StatusMessage data-aos="fade-up">{error}</StatusMessage>
      </ProfileContainer>
    );
  }

  if (!userInfo) return null;

  return (
    <ProfileContainer>
      <ProfileHeader data-aos="fade-up">
        <h1>{isDriver ? "Profil Chauffeur" : "Profil Client"}</h1>
      </ProfileHeader>

      <div data-aos="zoom-in">
        <ProfilePictureWrap>
          <ProfileAvatar
            role={isDriver ? "img" : "button"}
            aria-label={isDriver ? "Photo chauffeur" : "Changer la photo"}
            onClick={isDriver ? undefined : openFilePicker}
            $clickable={!isDriver}
            title={isDriver ? "Photo gérée par l’administration" : "Cliquer pour changer"}
          >
            {photo ? (
              <img src={getPhotoUrl(photo)} alt="profil" />
            ) : (
              <span>{isDriver ? "CH" : "CL"}</span>
            )}
          </ProfileAvatar>

          {!isDriver && (
            <>
              <HiddenFileInput
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                disabled={isUploading}
              />

              <UploadHint>
                {isUploading
                  ? "Upload en cours..."
                  : "Cliquer sur la photo pour uploader"}
              </UploadHint>
            </>
          )}

          {isDriver && (
            <UploadHint>
              Photo gérée par l’administration Dieli
            </UploadHint>
          )}
        </ProfilePictureWrap>
      </div>

      {isDriver && (
        <div data-aos="fade-up">
          <StatsGrid>
            <StatCard data-aos="fade-right">
              <StatLabel>Note chauffeur</StatLabel>
              <StatValue>
                {driverRating !== null && driverRating !== undefined
                  ? `${Number(driverRating).toFixed(1)} ★`
                  : "—"}
              </StatValue>
              <StatSub>Note moyenne reçue sur la plateforme</StatSub>
            </StatCard>

            <StatCard data-aos="fade-left">
              <StatLabel>Nombre de courses</StatLabel>
              <StatValue>{totalRides || 0}</StatValue>
              <StatSub>Total des courses effectuées</StatSub>
            </StatCard>
          </StatsGrid>
        </div>
      )}

      {isDriver && (
        <div data-aos="fade-up">
          <ReferralCard>
            <ReferralLabel>Code de parrainage</ReferralLabel>

            <ReferralCode>
              {referralCode || "Aucun code backend non renvoyé"}
            </ReferralCode>

            <ReferralSub>
              Partage ce code à un chauffeur pour qu’il se fasse parrainer.
            </ReferralSub>

            <CopyBtn
              type="button"
              onClick={handleCopyReferral}
              disabled={!referralCode}
            >
              Copier
            </CopyBtn>
          </ReferralCard>
        </div>
      )}

      {successMessage && (
        <StatusMessage success data-aos="fade-up">
          {successMessage}
        </StatusMessage>
      )}

      {error && <StatusMessage data-aos="fade-up">{error}</StatusMessage>}

      <div data-aos="fade-up">
        <ProfileDetails>
          <ProfileDetailItem data-aos="fade-up" data-aos-delay="0">
            <strong>Nom :</strong> {userInfo.firstName || "Non renseigné"}{" "}
            {userInfo.lastName || ""}
          </ProfileDetailItem>

          <ProfileDetailItem data-aos="fade-up" data-aos-delay="50">
            <strong>Email :</strong> {userInfo.email || "Non renseigné"}
          </ProfileDetailItem>

          <ProfileDetailItem data-aos="fade-up" data-aos-delay="100">
            <strong>Téléphone :</strong>{" "}
            {isDriver
              ? userInfo.phoneNumber || "Non renseigné"
              : userInfo.phone || userInfo.phoneNumber || "Non renseigné"}
          </ProfileDetailItem>

          {isDriver && (
            <ProfileDetailItem data-aos="fade-up" data-aos-delay="150">
              <strong>Genre :</strong> {userInfo.gender || "Non renseigné"}
            </ProfileDetailItem>
          )}

          <ProfileDetailItem data-aos="fade-up" data-aos-delay="200">
            <strong>Date d'inscription :</strong>{" "}
            {userInfo.createdAt
              ? new Date(userInfo.createdAt).toLocaleDateString("fr-FR")
              : "Non renseignée"}
          </ProfileDetailItem>

          {isDriver && (
            <ProfileDetailItem data-aos="fade-up" data-aos-delay="250">
              <strong>Statut chauffeur :</strong>{" "}
              {userInfo.status ||
                userInfo.driverStatus ||
                userInfo.validationStatus ||
                "Non renseigné"}
            </ProfileDetailItem>
          )}
        </ProfileDetails>
      </div>
    </ProfileContainer>
  );
};

export default Profile;